// Mercado Livre (plano canal-ml, F7): regras puras das telas. Sem React nem axios,
// para testar com node:test (web/test/ml-channel.test.ts). Nada aqui guarda nem mostra
// token ou segredo: o status que a API devolve nao os contem.

export type MlConnection = "conectado" | "desconectado" | "nao_conectado";

/** Resposta de GET /api/admin/channels/:slug/ml/status. */
export interface MlStatus {
  slug: string;
  connection: MlConnection;
  nickname: string | null;
  user_id: number | null;
  expires_at: string | null;
  refreshed_at: string | null;
  days_since_refresh: number | null;
  model: "user_products" | "legado" | null;
  last_error: { code?: string; at?: string } | null;
  has_client_id: boolean;
  has_client_secret: boolean;
}

export type Tone = "ok" | "warn" | "bad" | "idle";

export interface ConnectionView {
  tone: Tone;
  title: string;
  detail: string;
  /** Texto do botao principal; null = nao ha acao principal. */
  action: "Conectar ao Mercado Livre" | "Reconectar" | null;
  /** O botao principal fica desligado ate informar o Client Secret. */
  needsSecret: boolean;
}

function untilLabel(expiresAt: string | null, nowMs: number): string {
  if (!expiresAt) return "";
  const ms = Date.parse(expiresAt) - nowMs;
  if (!Number.isFinite(ms)) return "";
  if (ms <= 0) return "o acesso atual venceu e renova na próxima chamada";
  const min = Math.round(ms / 60_000);
  if (min < 90) return `o acesso atual vence em ${min} min e renova sozinho`;
  return `o acesso atual vence em ${Math.round(min / 60)} h e renova sozinho`;
}

export function modelLabel(model: MlStatus["model"]): string {
  if (model === "user_products") return "User Products (um anúncio por variação)";
  if (model === "legado") return "Anúncio com variações";
  return "ainda não identificado";
}

export function describeMlConnection(st: MlStatus, nowMs: number): ConnectionView {
  if (!st.has_client_secret) {
    return {
      tone: "warn",
      title: "Falta o Client Secret",
      detail: "Informe o Client Secret do app \"Vitrine Hiit\" para poder conectar.",
      action: st.connection === "conectado" ? "Reconectar" : "Conectar ao Mercado Livre",
      needsSecret: true,
    };
  }
  if (st.connection === "conectado") {
    const who = st.nickname ? `Conectado como ${st.nickname}` : "Conectado";
    return { tone: "ok", title: who, detail: untilLabel(st.expires_at, nowMs), action: "Reconectar", needsSecret: false };
  }
  if (st.connection === "desconectado") {
    return {
      tone: "bad",
      title: "Desconectado",
      detail: "O Mercado Livre recusou a renovação do acesso. Reconecte para voltar a publicar.",
      action: "Reconectar",
      needsSecret: false,
    };
  }
  return {
    tone: "idle",
    title: "Ainda não conectado",
    detail: "Clique em Conectar e entre no Mercado Livre como administrador da conta deste canal.",
    action: "Conectar ao Mercado Livre",
    needsSecret: false,
  };
}

// ---------------------------------------------------------------------------
// Volta do Mercado Livre: /admin/canais?ml=conectado | ?ml=erro&motivo=<codigo>
// ---------------------------------------------------------------------------
const MOTIVO: Record<string, string> = {
  nonce_invalido: "O pedido de conexão venceu ou já foi usado. Clique em Conectar de novo.",
  ml_negou: "O Mercado Livre não autorizou a conexão. Clique em Conectar e aceite o acesso.",
  sem_chave: "O sistema está sem a chave de cifra das credenciais. Avise o Wladimir.",
  canal_invalido: "Este canal não é do Mercado Livre.",
  sem_credencial: "Falta o Client ID ou o Client Secret do app neste canal. Informe o Client Secret e tente de novo.",
  conta_diferente: "Você entrou com outra conta do Mercado Livre. Entre como administrador da conta já conectada.",
  conta_ja_conectada: "Essa conta do Mercado Livre já está ligada a outro canal. Use um canal por conta (CNPJ).",
  troca_falhou: "O Mercado Livre não aceitou o código de acesso. Confira o Client Secret e tente de novo.",
  usuario_ml_falhou: "Não deu para ler os dados da conta no Mercado Livre. Tente de novo em instantes.",
  interno: "Algo falhou do nosso lado ao conectar. Tente de novo; se repetir, avise o Wladimir.",
};

export interface MlReturn {
  kind: "ok" | "erro";
  text: string;
}

/** Lê a query string de volta do OAuth. null = a página não veio do Mercado Livre. */
export function parseMlReturn(search: string): MlReturn | null {
  const p = new URLSearchParams(search);
  const ml = p.get("ml");
  if (ml === "conectado") return { kind: "ok", text: "Mercado Livre conectado." };
  if (ml === "erro") {
    const motivo = p.get("motivo") ?? "";
    return { kind: "erro", text: MOTIVO[motivo] ?? "Não deu para conectar ao Mercado Livre. Tente de novo." };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Mapa de categorias
// ---------------------------------------------------------------------------
/** Valor fixo de atributo da categoria (ex.: Tipo de roupa = Regata): o produto nao carrega, a categoria define. */
export interface FixedAttr {
  id: string;
  value_id?: string;
  value_name?: string;
}

export interface CategoryEntry {
  category_id?: string;
  fixed_attributes?: FixedAttr[];
  domain_id?: string;
  measure_type?: "BODY_MEASURE" | "CLOTHING_MEASURE" | "MIXED_MEASURE";
  columns_map?: Record<string, string>;
  size_chart?: boolean;
  filtrable_size_attr?: string;
  ml_model?: string;
  ml_gender?: string;
  ml_age_group?: string;
  ml_listing_type?: string;
  /** Tipo da peça para o título quando o nome do produto tem menos de 3 palavras. */
  ml_piece_type?: string;
  ml_pkg_height_cm?: number | string;
  ml_pkg_width_cm?: number | string;
  ml_pkg_length_cm?: number | string;
  ml_pkg_weight_g?: number | string;
}

/** Rascunho da tela: tudo texto, como o campo de formulário entrega. */
export interface CategoryDraft {
  category_id: string;
  domain_id: string;
  measure_type: string;
  size_chart: boolean;
  filtrable_size_attr: string;
  ml_gender: string;
  ml_age_group: string;
  ml_listing_type: string;
  ml_model: string;
  ml_piece_type: string;
  ml_pkg_height_cm: string;
  ml_pkg_width_cm: string;
  ml_pkg_length_cm: string;
  ml_pkg_weight_g: string;
  columns_map: Record<string, string>;
  /** Valores fixos da categoria (a tela "Ficha tecnica da categoria" edita; o resto do mapa os preserva). */
  fixed_attributes: FixedAttr[];
}

export const MEASURE_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "BODY_MEASURE", label: "Medidas do corpo" },
  { value: "CLOTHING_MEASURE", label: "Medidas da peça" },
  { value: "MIXED_MEASURE", label: "Corpo e peça" },
];

export const GENDERS = ["Feminino", "Masculino", "Sem gênero", "Sem gênero infantil", "Meninas", "Meninos"] as const;
export const AGE_GROUPS = ["Adultos", "Crianças", "Bebês", "Adolescentes"] as const;
export const LISTING_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "gold_special", label: "Clássico" },
  { value: "gold_pro", label: "Premium" },
];

/** Medidas que o ML aceita na tabela (sugestões; o campo é livre). */
export const MEASURE_ATTR_SUGGESTIONS = [
  "BUST_CIRCUMFERENCE_FROM",
  "WAIST_CIRCUMFERENCE_FROM",
  "HIP_CIRCUMFERENCE_FROM",
  "GARMENT_LENGTH_FROM",
  "GARMENT_WAIST_WIDTH_FROM",
] as const;

export const emptyDraft = (): CategoryDraft => ({
  category_id: "", domain_id: "", measure_type: "BODY_MEASURE", size_chart: true, filtrable_size_attr: "",
  ml_gender: "", ml_age_group: "Adultos", ml_listing_type: "", ml_model: "", ml_piece_type: "",
  ml_pkg_height_cm: "", ml_pkg_width_cm: "", ml_pkg_length_cm: "", ml_pkg_weight_g: "", columns_map: {}, fixed_attributes: [],
});

export function entryToDraft(e: CategoryEntry | null | undefined): CategoryDraft {
  const d = emptyDraft();
  if (!e) return d;
  return {
    category_id: e.category_id ?? "",
    domain_id: e.domain_id ?? "",
    measure_type: e.measure_type ?? "BODY_MEASURE",
    size_chart: e.size_chart !== false,
    filtrable_size_attr: e.filtrable_size_attr ?? "",
    ml_gender: e.ml_gender ?? "",
    ml_age_group: e.ml_age_group ?? "Adultos",
    ml_listing_type: e.ml_listing_type ?? "",
    ml_model: e.ml_model ?? "",
    ml_piece_type: e.ml_piece_type ?? "",
    ml_pkg_height_cm: e.ml_pkg_height_cm != null ? String(e.ml_pkg_height_cm) : "",
    ml_pkg_width_cm: e.ml_pkg_width_cm != null ? String(e.ml_pkg_width_cm) : "",
    ml_pkg_length_cm: e.ml_pkg_length_cm != null ? String(e.ml_pkg_length_cm) : "",
    ml_pkg_weight_g: e.ml_pkg_weight_g != null ? String(e.ml_pkg_weight_g) : "",
    columns_map: { ...(e.columns_map ?? {}) },
    fixed_attributes: (e.fixed_attributes ?? []).map((f) => ({ ...f })),
  };
}

/** "MLB-T_SHIRTS" -> "T_SHIRTS_FILTRABLE_SIZES" (padrão do atributo de tamanho filtrável do ML). */
export function suggestFiltrableAttr(domainId: string): string {
  const m = /^MLB-([A-Z0-9_]+)$/.exec(domainId.trim());
  return m ? `${m[1]}_FILTRABLE_SIZES` : "";
}

const PKG: ReadonlyArray<keyof CategoryDraft> = ["ml_pkg_height_cm", "ml_pkg_width_cm", "ml_pkg_length_cm", "ml_pkg_weight_g"];
export const PKG_LABEL: Record<string, string> = {
  ml_pkg_height_cm: "Altura da embalagem (cm)",
  ml_pkg_width_cm: "Largura da embalagem (cm)",
  ml_pkg_length_cm: "Comprimento da embalagem (cm)",
  ml_pkg_weight_g: "Peso da embalagem (g)",
};

/** Erros do rascunho, em português, por campo. Vazio = pode salvar. */
export function validateDraft(d: CategoryDraft): Record<string, string> {
  const err: Record<string, string> = {};
  if (!/^MLB\d+$/.test(d.category_id.trim())) err.category_id = "Informe a categoria do ML (ex.: MLB108716). Use Sugerir.";
  if (d.domain_id.trim() && !/^MLB-[A-Z0-9_]+$/.test(d.domain_id.trim())) err.domain_id = "Domínio no formato MLB-T_SHIRTS.";
  for (const k of PKG) {
    const v = String(d[k]).trim();
    if (v === "") continue;
    const n = Number(v);
    if (!Number.isInteger(n) || n <= 0) err[k as string] = `${PKG_LABEL[k as string]}: número inteiro maior que zero.`;
  }
  for (const [col, attr] of Object.entries(d.columns_map)) {
    if (attr.trim() && !/^[A-Z0-9_]{2,60}$/.test(attr.trim())) err[`col:${col}`] = `Medida do ML da coluna "${col}": código em maiúsculas (ex.: WAIST_CIRCUMFERENCE_FROM).`;
  }
  return err;
}

/** Rascunho -> entrada gravável. Campo vazio some (cai para o padrão do canal). */
export function draftToEntry(d: CategoryDraft): CategoryEntry {
  const out: CategoryEntry = { category_id: d.category_id.trim() };
  const s = (v: string) => v.trim();
  if (s(d.domain_id)) out.domain_id = s(d.domain_id);
  if (s(d.ml_model)) out.ml_model = s(d.ml_model);
  if (s(d.ml_piece_type)) out.ml_piece_type = s(d.ml_piece_type);
  if (s(d.ml_gender)) out.ml_gender = s(d.ml_gender);
  if (s(d.ml_age_group)) out.ml_age_group = s(d.ml_age_group);
  if (s(d.ml_listing_type)) out.ml_listing_type = s(d.ml_listing_type);
  if (d.size_chart) {
    out.size_chart = true;
    if (s(d.measure_type)) out.measure_type = s(d.measure_type) as CategoryEntry["measure_type"];
    if (s(d.filtrable_size_attr)) out.filtrable_size_attr = s(d.filtrable_size_attr);
    const cm = Object.fromEntries(Object.entries(d.columns_map).map(([k, v]) => [k, v.trim()] as const).filter(([, v]) => v !== ""));
    if (Object.keys(cm).length > 0) out.columns_map = cm;
  } else {
    out.size_chart = false;
  }
  for (const k of PKG) {
    const v = String(d[k]).trim();
    if (v !== "") (out as Record<string, unknown>)[k as string] = Number(v);
  }
  const fixed = d.fixed_attributes.filter((f) => f.id.trim() !== "" && (f.value_id?.trim() || f.value_name?.trim()));
  if (fixed.length > 0) out.fixed_attributes = fixed.map((f) => (f.value_id?.trim() ? { id: f.id.trim(), value_id: f.value_id.trim() } : { id: f.id.trim(), value_name: f.value_name!.trim() }));
  return out;
}

/** Valor escolhido para o atributo fixo (id do valor da lista do ML, ou o texto livre). "" = sem valor fixo. */
export function fixedValueOf(list: readonly FixedAttr[], attrId: string): string {
  const f = list.find((x) => x.id === attrId);
  return f?.value_id ?? f?.value_name ?? "";
}

/** Troca (ou tira, com valor vazio) o valor fixo de um atributo. Com lista fechada grava value_id; sem lista, value_name. */
export function withFixed(list: readonly FixedAttr[], attrId: string, value: string, closedList: boolean): FixedAttr[] {
  const rest = list.filter((x) => x.id !== attrId);
  const v = value.trim();
  if (v === "") return rest;
  return [...rest, closedList ? { id: attrId, value_id: v } : { id: attrId, value_name: v }];
}

/** Mapa novo = mapa atual (inclui categorias que a tela não lista) com a categoria editada trocada ou removida. */
export function applyCategoryEdit(
  current: Record<string, CategoryEntry>,
  categoryId: number,
  draft: CategoryDraft | null,
): Record<string, CategoryEntry> {
  const next = { ...current };
  if (draft === null) delete next[String(categoryId)];
  else next[String(categoryId)] = draftToEntry(draft);
  return next;
}

export function unmappedText(unmapped: number, total: number): string {
  if (total === 0) return "Nenhum produto pronto para o Mercado Livre ainda (ativo no Sisplan e com foto do canal).";
  if (unmapped === 0) return `Todos os ${total} produtos prontos têm categoria do ML.`;
  return `${unmapped} de ${total} produtos prontos ficam sem categoria do ML e não serão publicados até mapear.`;
}

// ---------------------------------------------------------------------------
// Ficha do produto: estado do anúncio
// ---------------------------------------------------------------------------
export interface ProductMl {
  item_id: string | null;
  model: string | null;
  status: string | null;
  sub_status: string[];
  permalink: string | null;
}

export interface ItemView {
  tone: Tone;
  title: string;
  detail: string;
}

const MODERATION = new Set(["moderation_penalty", "waiting_for_patch", "forbidden", "suspended", "pending_documentation", "held", "warning"]);

export function describeMlItem(ml: ProductMl | null): ItemView | null {
  if (!ml) return null;
  if (ml.status === "creating") {
    return { tone: "warn", title: "Criando o anúncio no Mercado Livre", detail: "A última tentativa não terminou; a próxima publicação continua de onde parou." };
  }
  if (!ml.item_id) return { tone: "idle", title: "Ainda não publicado no Mercado Livre", detail: "Publique pelo botão do rodapé quando a categoria estiver mapeada." };
  const sub = ml.sub_status ?? [];
  switch (ml.status) {
    case "active":
      return { tone: "ok", title: "Anúncio ativo no Mercado Livre", detail: ml.item_id };
    case "paused":
      if (sub.some((s) => MODERATION.has(s))) return { tone: "bad", title: "Pausado pelo Mercado Livre", detail: `${ml.item_id} · ${sub.join(", ")}` };
      if (sub.includes("out_of_stock")) return { tone: "warn", title: "Pausado por falta de estoque", detail: `${ml.item_id} · volta sozinho quando entrar estoque` };
      return { tone: "warn", title: "Anúncio pausado", detail: ml.item_id };
    case "under_review":
      return { tone: "bad", title: "Em revisão no Mercado Livre", detail: `${ml.item_id}${sub.length ? ` · ${sub.join(", ")}` : ""}` };
    case "closed":
      return { tone: "bad", title: "Anúncio encerrado", detail: `${ml.item_id} · publicar de novo cria um anúncio novo` };
    default:
      return { tone: "idle", title: "Anúncio no Mercado Livre", detail: `${ml.item_id}${ml.status ? ` · ${ml.status}` : ""}` };
  }
}

// ---------------------------------------------------------------------------
// Apelidos de tamanho (config.ml_size_aliases): tamanho no Vitrine -> tamanho na grade do ML
// ---------------------------------------------------------------------------
/** Linhas da seção: tamanhos em uso (na ordem da API) + os já apelidados que não estão em uso. */
export function sizeAliasRows(sizesInUse: readonly string[], aliases: Readonly<Record<string, string>>): string[] {
  const seen = new Set<string>();
  const rows: string[] = [];
  for (const s of sizesInUse) {
    if (!seen.has(s)) {
      seen.add(s);
      rows.push(s);
    }
  }
  const extra = Object.keys(aliases)
    .filter((k) => !seen.has(k))
    .sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }));
  return [...rows, ...extra];
}

/** Mapa gravável: tira chave vazia, valor vazio e apelido igual ao próprio tamanho (vazio = igual). */
export function cleanSizeAliases(aliases: Readonly<Record<string, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(aliases)) {
    const key = k.trim();
    const val = v.trim();
    if (key === "" || val === "" || val === key) continue;
    out[key] = val;
  }
  return out;
}

export function sameSizeAliases(a: Readonly<Record<string, string>>, b: Readonly<Record<string, string>>): boolean {
  const ca = cleanSizeAliases(a);
  const cb = cleanSizeAliases(b);
  const ka = Object.keys(ca);
  return ka.length === Object.keys(cb).length && ka.every((k) => ca[k] === cb[k]);
}

// ---------------------------------------------------------------------------
// Pendências da publicação automática (GET /api/channels/:slug/ml/pendencies)
// ---------------------------------------------------------------------------
export interface MlPendencyReason {
  code: string;
  text: string;
}
export interface MlPendencyItem {
  sku: string;
  name: string;
  state: "pending" | "queued";
  reasons: MlPendencyReason[];
  stock: number;
  photos: number;
  has_publication: boolean;
  evaluated_at: string | null;
  retry_after: string | null;
}
export interface MlPendencies {
  slug: string;
  enabled: boolean;
  last_run_at: string | null;
  summary: { live: number; queued: number; pending: number; excluded: number };
  items: MlPendencyItem[];
}

const REASON_LABELS: Record<string, string> = {
  no_price: "Sem preço",
  no_size_guide: "Tabela de medidas",
  size_chart_off: "Tabela de medidas",
  pkg_missing: "Envio",
  title_short: "Título",
  size_no_equivalent: "Tamanho sem equivalência",
  no_photo: "Sem foto",
  no_category_map: "Categoria sem mapa",
  ml_recusou: "Mercado Livre recusou",
  size_not_in_guide: "Tabela de medidas",
  no_domain: "Tabela de medidas",
  pkg_invalid: "Envio",
  title_empty: "Título",
  title_too_long: "Título",
  no_gender: "Gênero",
  mixed_prices: "Preço",
  no_size_color: "Cor e tamanho",
  ean_invalid: "EAN",
  ean_duplicate: "EAN",
  gtin_required: "EAN",
  gtin_reason: "EAN",
  attr_required: "Atributo obrigatório",
  sem_cadastro_sisplan: "Sem cadastro no Sisplan",
  ml_aguardando: "Em espera",
  aguardando_vez: "Na fila do ciclo",
  ml_indisponivel: "Mercado Livre indisponível",
};
export const OTHER_REASON = "other";

/** Etiqueta de agrupamento do motivo; código desconhecido cai em "Outro". */
export function reasonLabel(code: string): string {
  return Object.prototype.hasOwnProperty.call(REASON_LABELS, code) ? REASON_LABELS[code]! : "Outro";
}
/** Chave de filtro do motivo: o próprio código se conhecido, senão "other". */
export function reasonGroup(code: string): string {
  return Object.prototype.hasOwnProperty.call(REASON_LABELS, code) ? code : OTHER_REASON;
}

export interface ReasonChip {
  group: string;
  label: string;
  count: number;
}
/** Chips de filtro: quantos SKUs têm cada grupo de motivo (um SKU conta uma vez por grupo), maior primeiro. */
export function reasonChips(items: readonly MlPendencyItem[]): ReasonChip[] {
  const counts = new Map<string, number>();
  for (const it of items) {
    for (const g of new Set(it.reasons.map((r) => reasonGroup(r.code)))) counts.set(g, (counts.get(g) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([group, count]) => ({ group, label: reasonLabel(group), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "pt-BR"));
}

const norm = (s: string): string => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Filtra por grupo de motivo (null = todos) e por busca de SKU/nome (sem acento, sem caixa). */
export function filterPendencies(items: readonly MlPendencyItem[], group: string | null, query: string): MlPendencyItem[] {
  const q = norm(query.trim());
  return items.filter((it) => {
    if (group && !it.reasons.some((r) => reasonGroup(r.code) === group)) return false;
    if (q && !norm(it.sku).includes(q) && !norm(it.name).includes(q)) return false;
    return true;
  });
}
